import {
	useState,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	PropsWithChildren,
	MouseEvent,
	UIEvent
} from 'react';
import { createPortal } from 'react-dom';
import styles from './FloatingMenu.module.scss';
import { FloatingMenuContext } from './FloatingMenuContext';
import CloseButton from '@mui/icons-material/Close';
import { TObject } from '@types';

type TProps = {
	menuPos?: 'left' | 'center' | 'right' | 'full-right' | 'full-left';
	borderRadius?: number;
	offset?: number;
	showCloseButton?: boolean;
	closeOnClick?: boolean;
}

export const Menu = (props:PropsWithChildren<TProps>) => {
	const {
		menuPos = 'center',
		borderRadius = 10,
		offset = 5,
		showCloseButton = false,
		closeOnClick = true,
		children
	} = props;
	const ctx = useContext(FloatingMenuContext);
	const [isAboveFold, setIsAboveFold] = useState(true);
	const [showMenu, setShowMenu] = useState(false);
	const menuRef = useRef<HTMLDivElement | null>(null);
	const scrollTopRef = useRef(0);
	const handleScroll = (e:UIEvent<HTMLDivElement>) => {
		scrollTopRef.current = e.currentTarget.scrollTop;
	}
	const setPosition = () => {
		if (ctx?.parentRef && menuRef.current) {
			const { offsetHeight, offsetWidth } = ctx.parentRef;
			const { left, top } = ctx.parentRef.getBoundingClientRect();
			const { scrollHeight: oHeight, offsetWidth: oWidth } = menuRef.current;
			const styles:TObject = {};
			let xPos = 0;
			switch (menuPos) {
			case 'left':
				xPos = left;
				break;
			case 'right':
				xPos = left + offsetWidth - oWidth;
				break;
			case 'full-right':
				xPos = left + offsetWidth;
				break;
			default:
				xPos = left - (oWidth);
			}
			styles.position = 'fixed';
			styles.left = 0;
			styles.top = 0;
			styles.minWidth = 'max-content';
			styles.willChange = 'transform';
			if (isAboveFold) {
				const posTop = (menuPos === 'full-left' || menuPos === 'full-right') ? `${top + offset}` : `${offsetHeight + top + offset}`;
				styles.transform = `translate(${xPos}px, ${posTop}px)`;
				if (oHeight > window.innerHeight - offsetHeight - top - offset) {
					styles.height = window.innerHeight - offsetHeight - top - offset - 10;
					styles.overflow = 'auto';
				}
			} else {
				const tempTop = top - oHeight - offset;
				styles.transform = `translate(${xPos}px, ${tempTop < 0 ? 10 : tempTop}px)`;
				if (tempTop < 0) {
					styles.height = top - 10;
					styles.overflow = 'auto';
				}
			}
			return styles;
		}
		return {}
	}
	const setStyles = () => {
		const styles:TObject = {};
		styles.borderRadius = `${borderRadius}px`;
		return styles;
	}
	const preventBubble = (e:MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		if (closeOnClick && ctx?.toggleMenu) ctx?.toggleMenu(false);
	}
	useEffect(() => {
		if (ctx?.showMenu && ctx?.parentRef) {
			const { offsetHeight } = ctx.parentRef;
			const { top } = ctx.parentRef.getBoundingClientRect();
			let oHeight = 0;
			if (menuRef && menuRef.current) oHeight = menuRef.current.offsetHeight;
			setIsAboveFold((top + offsetHeight) < (window.innerHeight / 2) ? true : window.innerHeight - (offsetHeight + top) - oHeight > 0);
			const showMenuTimeout = setTimeout(() => {
				setShowMenu(true)
			}, 100);
			return () => clearTimeout(showMenuTimeout);
		}
		setShowMenu(false);
	}, [ctx?.showMenu]);

	useLayoutEffect(() => {
		if (menuRef.current && scrollTopRef.current > 0) {
			menuRef.current.scrollTop = scrollTopRef.current;
		}
	});

	return (
		<>
			{ctx?.showMenu && createPortal(<div className={styles.floatingMenuContainer}><div data-testid={'@Ofload-FloatingMenu-Menu'} onClick={(e) => preventBubble(e)} onScroll={handleScroll} className={`floating-menu-menu ${styles.menu} ${showMenu ? styles.showMenu : ''}`} style={{ ...setPosition(), ...setStyles() }} ref={menuRef}>
				{showCloseButton && <button data-testid={'@Ofload-FloatingMenu-CloseBtn'} className={styles.closeBtn} onClick={() => { ctx?.toggleMenu(false) }} aria-label={'Close Floating Menu Button'}><CloseButton /></button>}
				{children}
			</div></div>, document.body)}
		</>
	);
}
